import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { usePrefs, useLabels } from 'wsm-common-data';
import formatDate from '../utils/formatDate';
import FinancingDetails from './FinancingDetails';
import IncentiveTrims from './IncentiveTrims';
import TrimsPopup from './TrimsPopup';
import ActionButtons from './ActionButtons';

const IncentiveItem = ({ offer, vehicle }) => {
	const prefs = usePrefs();
	const labels = useLabels();
	const [trimsModalOpen, setTrimsModalOpen] = useState(false);

	const showShortTitle = prefs.showShortTitle !== 'false';
	const showImage = prefs.showIncentiveImage === 'true';
	const showImageDisclaimer = prefs.showIncentivesImageDisclaimer === 'true';
	const trimsInline =
		prefs.showIncentiveTrimsInOfferDetails === 'true' ||
		prefs.showIncentiveTrimsInOfferDetails === 'compliant';
	const hasTrims = offer.trims?.length > 0;
	const showTrimLink = !trimsInline && hasTrims;

	const title = showShortTitle ? offer.i18NShortTitle : offer.i18NTitle;

	const hasImage = showImage && offer.imageUrl;
	const hasDisclaimer = Boolean(offer.i18NDisclaimer);

	const effectiveFormatted = formatDate(offer.effectiveDate);
	const expirationFormatted = formatDate(offer.expirationDate);
	const hasDates = effectiveFormatted && expirationFormatted;

	const imageDisclaimerLabel = labels.get('INCENTIVES_IMAGES_DISCLAIMER');
	const seeTrimsLabel = labels.get('INCENTIVES_SEE_TRIMS');

	return (
		<article className="article offers-listing p-3 mb-3" aria-label={title}>
			<div className="row">
				{hasImage && (
					<div className="col-md-4 d-none d-md-block">
						<img
							src={offer.imageUrl}
							alt=""
							aria-label={`${title} illustration`}
							className="img-fluid"
						/>
						{showImageDisclaimer && (
							<p className="text-muted ddc-font-size-xsmall mt-1">
								{imageDisclaimerLabel}
							</p>
						)}
					</div>
				)}

				<div className={hasImage ? 'col-md-8' : 'col-12'}>
					<h3 className="mb-1">{title}</h3>

					<ul className="mb-2 ml-5">
						{offer.i18NOfferDetails && (
							<li>{offer.i18NOfferDetails}</li>
						)}

						{!showShortTitle && <FinancingDetails offer={offer} />}

						{hasDates && (
							<li>
								Offer only valid {effectiveFormatted} through{' '}
								{expirationFormatted}
							</li>
						)}
					</ul>

					<IncentiveTrims offer={offer} />

					{hasDisclaimer && (
						<p className="text-muted ddc-font-size-xsmall mt-2">
							{offer.i18NDisclaimer}
						</p>
					)}

					{showTrimLink && (
						<button
							type="button"
							className="btn btn-link p-0 mt-2"
							onClick={() => setTrimsModalOpen(true)}
						>
							{seeTrimsLabel}
						</button>
					)}

					{showTrimLink && (
						<TrimsPopup
							isOpen={trimsModalOpen}
							onClose={() => setTrimsModalOpen(false)}
							offer={offer}
						/>
					)}

					<ActionButtons offer={offer} vehicle={vehicle} />
				</div>
			</div>
		</article>
	);
};

IncentiveItem.propTypes = {
	offer: PropTypes.shape({
		incentiveId: PropTypes.string.isRequired,
		incentiveType: PropTypes.string.isRequired,
		i18NTitle: PropTypes.string,
		i18NShortTitle: PropTypes.string,
		i18NOfferDetails: PropTypes.string,
		i18NDisclaimer: PropTypes.string,
		effectiveDate: PropTypes.string,
		expirationDate: PropTypes.string,
		imageUrl: PropTypes.string,
		trims: PropTypes.arrayOf(
			PropTypes.shape({
				trim: PropTypes.string
			})
		),
		incentiveData: PropTypes.shape({
			cashOption: PropTypes.number,
			lenderName: PropTypes.string,
			bestAprTerm: PropTypes.shape({
				term: PropTypes.number,
				value: PropTypes.number
			}),
			aprTerms: PropTypes.arrayOf(
				PropTypes.shape({
					term: PropTypes.number,
					value: PropTypes.number
				})
			),
			monthlyPayment: PropTypes.number,
			cashDue: PropTypes.number
		})
	}).isRequired,
	vehicle: PropTypes.shape({
		globalVehicleId: PropTypes.string
	}).isRequired
};

export default IncentiveItem;
